import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NaturalLanguageQuery = ({ onQuery, isLoading, selectedDocumentBase, documentBaseName }) => {
  const [query, setQuery] = useState('');
  const [queryHistory, setQueryHistory] = useState([]);
  const [isProcessingQuery, setIsProcessingQuery] = useState(false);

  const suggestedQueries = [
    "What do our policies say about activities at 30, 60, 90, or 180 day intervals?",
    "How often are privileged users required to change their passwords?", 
    "What are the password complexity requirements?",
    "What is our incident response procedure?",
    "What are the data retention policies?",
    "What security training is required for employees?",
    "What are the backup and recovery procedures?",
    "What is our acceptable use policy for technology?",
    "What are the access control requirements?",
    "What compliance requirements do we need to meet?"
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query?.trim() || isProcessingQuery || isLoading) return;

    console.log('Processing query:', query, 'with document base:', selectedDocumentBase);
    
    setIsProcessingQuery(true);
    
    const newQuery = {
      id: Date.now(),
      question: query,
      answer: "Analyzing your documents for relevant information...",
      timestamp: new Date()?.toLocaleString(),
      isProcessing: true,
      documentBase: documentBaseName
    };

    setQueryHistory(prev => [newQuery, ...prev]);
    
    try {
      console.log('Calling onQuery function...');
      const response = await onQuery(query, selectedDocumentBase);
      console.log('Received response:', response);
      
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { ...item, answer: response || "No response received from AI service.", isProcessing: false }
            : item
        )
      );
    } catch (error) {
      console.error('Query processing error:', error);
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { 
                ...item, 
                answer: `Error processing query: ${error?.message || 'Unknown error occurred'}. This might be due to AI service configuration issues, API limits, or connectivity problems. Please check your OpenAI API key and try again.`, 
                isProcessing: false,
                hasError: true
              }
            : item
        )
      );
    } finally {
      setIsProcessingQuery(false);
    }

    setQuery('');
  };

  const handleSuggestedQuery = async (suggestedQuery) => {
    if (isProcessingQuery || isLoading) return;
    
    console.log('Processing suggested query:', suggestedQuery);
    
    setQuery(suggestedQuery);
    setIsProcessingQuery(true);
    
    const newQuery = {
      id: Date.now(),
      question: suggestedQuery,
      answer: "Analyzing your documents for relevant information...",
      timestamp: new Date()?.toLocaleString(),
      isProcessing: true,
      documentBase: documentBaseName
    };

    setQueryHistory(prev => [newQuery, ...prev]);
    
    try {
      const response = await onQuery(suggestedQuery, selectedDocumentBase);
      console.log('Suggested query response:', response);
      
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { ...item, answer: response || "No response received from AI service.", isProcessing: false }
            : item
        )
      );
    } catch (error) {
      console.error('Suggested query processing error:', error);
      setQueryHistory(prev => 
        prev?.map(item => 
          item?.id === newQuery?.id 
            ? { 
                ...item, 
                answer: `Error processing query: ${error?.message || 'Unknown error'}. Please try again later or check your AI service configuration.`, 
                isProcessing: false,
                hasError: true
              }
            : item
        )
      );
    } finally {
      setIsProcessingQuery(false);
    }

    setQuery('');
  };

  return (
    <div className="space-y-6">
      {/* Processing Status Alert */}
      {isProcessingQuery && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="Loader2" size={20} className="text-blue-600 animate-spin" />
            <div>
              <p className="text-sm font-medium text-blue-800">Analyzing Document Content...</p>
              <p className="text-xs text-blue-700 mt-1">
                Reading through your documents to provide accurate, content-based responses.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Query Input */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Ask about your documents
          </label>
          <textarea
            placeholder="Ask me anything about your document content. For example: 'What do our policies say about password requirements?' or 'What are our data retention policies?'"
            value={query}
            onChange={(e) => setQuery(e?.target?.value)}
            disabled={isProcessingQuery || isLoading}
            rows={4}
            className="w-full px-4 py-3 text-sm bg-background border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed placeholder:text-muted-foreground"
          />
        </div>
        <div className="flex justify-between items-center">
          <p className="text-xs text-muted-foreground">
            Querying: <span className="font-medium">{documentBaseName}</span>
          </p>
          <Button
            type="submit"
            disabled={!query?.trim() || isProcessingQuery || isLoading}
            loading={isProcessingQuery}
            iconName="Send"
            iconPosition="right"
            iconSize={16}
            size="lg"
          >
            Ask Zentri
          </Button>
        </div>
      </form>

      {/* Suggested Queries */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3">Popular Questions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {suggestedQueries?.map((suggestedQuery, index) => (
            <button
              key={index}
              onClick={() => handleSuggestedQuery(suggestedQuery)}
              disabled={isProcessingQuery || isLoading}
              className="text-left px-4 py-3 text-sm bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-border hover:border-primary/20"
            >
              <Icon name="MessageCircle" size={14} className="inline mr-2" />
              {suggestedQuery}
            </button>
          ))}
        </div>
      </div>

      {/* Query History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-foreground">Conversation History</h3>
          {queryHistory?.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setQueryHistory([])}
              iconName="Trash2"
              iconPosition="left"
              iconSize={14}
            >
              Clear History
            </Button>
          )}
        </div>
        
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {queryHistory?.length > 0 ? (
            queryHistory?.map((item) => (
              <div key={item?.id} className="border border-border rounded-lg p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{item?.question}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>{item?.timestamp}</span>
                      <span>• {item?.documentBase}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    {item?.isProcessing ? (
                      <Icon name="Loader2" size={18} className="text-accent animate-spin" />
                    ) : (
                      <Icon name="Bot" size={18} className="text-accent" />
                    )}
                  </div>
                  <div className={`flex-1 rounded-lg p-4 ${item?.hasError ? 'bg-destructive/10 border border-destructive/20' : 'bg-muted/50'}`}>
                    <div className="text-sm text-foreground whitespace-pre-line leading-relaxed">
                      {item?.answer}
                    </div>
                    {item?.hasError && (
                      <div className="mt-3 text-xs text-destructive flex items-center gap-1">
                        <Icon name="AlertTriangle" size={12} />
                        AI service error - check configuration or try a simpler query
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-muted/50 rounded-lg border border-dashed border-border">
              <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-2">
                No conversations yet
              </p>
              <p className="text-xs text-muted-foreground">
                Start by asking a question or selecting from the popular questions above
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Notices */}
      {!import.meta.env?.VITE_OPENAI_API_KEY && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={18} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-800">AI Configuration Required</p>
              <p className="text-xs text-yellow-700 mt-1">
                To enable AI-powered document analysis, please add your OpenAI API key to the VITE_OPENAI_API_KEY environment variable.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NaturalLanguageQuery;